import { useCallback, useEffect, useRef, useState } from 'react'
import { loadTurnstile } from '../services/turnstile/loadTurnstile'
import {
  TurnstileController,
  TurnstileState,
  createTurnstileController,
} from '../services/turnstile/turnstileController'

interface Options {
  /** Cloudflare site key. Leave it out to skip the challenge entirely. */
  siteKey?: string
  /** Names the form to siteverify, e.g. 'login'. */
  action: string
  /** ISO 639-1 code for the widget's own text; 'auto' follows the browser. */
  language?: string
}

/**
 * Mounts an invisible Turnstile widget in `containerRef` for the life of the
 * form and hands out fresh tokens on demand. `getToken` resolves `null` when
 * no site key is configured, so callers can send nothing rather than branch.
 * `isVerified` says a token is ready to hand out, so forms can keep submit
 * disabled until then; with no site key there is nothing to wait for.
 * `hasFailed` says the challenge failed or the widget could not be drawn at
 * all (script or frame blocked), so the form can explain why submit is off.
 */
export const useTurnstile = ({ siteKey, action, language }: Options) => {
  const containerRef = useRef<HTMLDivElement>(null)
  const controllerRef = useRef<Promise<TurnstileController> | null>(null)
  const [state, setState] = useState<TurnstileState>('verifying')

  useEffect(() => {
    const container = containerRef.current
    if (!siteKey || !container) return

    let disposed = false
    const controller = loadTurnstile().then((api) => {
      const created = createTurnstileController(api, container, {
        siteKey,
        action,
        language,
        onStateChange: (next) => {
          if (!disposed) setState(next)
        },
      })
      // The form went away while the script was still loading.
      if (disposed) created.dispose()
      return created
    })
    controllerRef.current = controller
    controller.catch(() => {
      if (!disposed) setState('failed')
    })

    return () => {
      disposed = true
      controllerRef.current = null
      setState('verifying')
      controller.then((created) => created.dispose()).catch(() => {})
    }
  }, [siteKey, action, language])

  const getToken = useCallback(async (): Promise<string | null> => {
    if (!siteKey) return null
    if (!controllerRef.current) {
      throw new Error('Turnstile container is not mounted')
    }
    const controller = await controllerRef.current
    return controller.getToken()
  }, [siteKey])

  return {
    containerRef,
    getToken,
    isVerified: !siteKey || state === 'ready',
    hasFailed: state === 'failed',
  }
}
